/* eslint-disable camelcase */
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTableStateEvent } from 'primereact/datatable';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import { classNames } from 'primereact/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EmptyProductsImg from '../../../../assets/images/empty-products.svg';
import EmptyScreen from '../../../../components/empty-screen/EmptyScreen';
import { useGetPaginatedProducts } from '../../../../hooks/api/products';
import { useListTags } from '../../../../hooks/api/tag';
import { useAuth } from '../../../../hooks/auth/useAuth';
import useDialogContext from '../../../../hooks/dialog/useDialogContext';
import { useProductsTableQueryParams } from '../../../../hooks/useProductsTableQueryParams';
import EmissionValueWithSuffix from '../../../../shared/components/emission-value-with-suffix/EmissionValueWithSuffix';
import {
  CountryCodeToLabelMap,
  RegionCodeToLabelMap,
} from '../../../../shared/helpers/country-region-helpers';
import { getPaginatorTemplate } from '../../../../shared/helpers/get-paginator-template';
import { EmissionSymbol } from '../../../../shared/interfaces/IFootprint';
import { IProduct } from '../../../../shared/interfaces/IProduct';
import ProductName from '../product-name/ProductName';
import ProductsHeader from '../products-header/ProductsHeader';
import styles from './ProductsTable.module.css';

const ProductsTable = (): JSX.Element => {
  // Table params from URL
  const { skip, size, sort, filters, setParams } = useProductsTableQueryParams();

  const [sortField, sortOrder] = sort ? sort.split('-') : [undefined, undefined];

  const [totalRecords, setTotalRecords] = useState<number>(0);

  const { t } = useTranslation();
  const { openDialog } = useDialogContext();

  const { isBasfNonAdmin, user } = useAuth();

  // RQ Query to get products
  const { data, isLoading } = useGetPaginatedProducts({
    ...filters,
    sort,
    skip,
    size,
  });

  useEffect(() => {
    if (data) {
      setTotalRecords(data?.pagination?.total || 0);
    }
  }, [data]);

  const productNameTemplate = (rowData: IProduct): JSX.Element => {
    return <ProductName product={rowData} />;
  };

  const locationTemplate = (rowData: IProduct): string | null => {
    if (rowData.country) {
      return CountryCodeToLabelMap[rowData.country] || rowData.country;
    } else if (rowData.region) {
      return RegionCodeToLabelMap[rowData.region] || rowData.region;
    }

    return '';
  };

  const pcfTemplate = (rowData: IProduct): JSX.Element | string => {
    const emissionValue = rowData?.pcf?.pcfExcludingBiogenic ?? 0;

    return (
      <EmissionValueWithSuffix
        emissionValue={emissionValue}
        emissionValueDecimals={3}
        emissionSymbol={EmissionSymbol.KG_CO2E}
        declaredUnit={rowData?.pcf?.activeCarbonFootprintDeclaredUnit}
        abbreviateUnit
        unitaryProductAmount={rowData?.pcf?.activeCarbonFootprintUnitaryProductAmount}
      />
    );
  };

  const canUseTags = user?.company?.license !== 'basic';

  const { data: companyTags } = useListTags({ enabled: canUseTags });

  const tagsTemplate = (rowData: IProduct): JSX.Element | string => {
    if (rowData.tags.length <= 0) {
      return '';
    }

    return (
      <div className='flex flex-row gap-1 -my-1'>
        <Tag
          className='bg-white text-900 font-medium px-2 py-0 border-1 border-round overflow-hidden'
          value={rowData.tags[0].name}
          pt={{
            value: {
              className: 'white-space-nowrap text-overflow-ellipsis overflow-hidden max-w-full',
            },
          }}
        />
        {rowData.tags.length > 1 && (
          <div className={classNames(`dynamic-tags-${rowData.productId}`)}>
            <Tag
              className={
                'text-900 font-medium px-2 py-0 border-1 cursor-pointer bg-white hover:bg-primary-100 hover:border-primary-500'
              }
              value={`+${rowData.tags.length - 1}`}
              id={`${rowData.id}`}
            />
            <Tooltip
              target={`.dynamic-tags-${rowData.productId}`}
              className={classNames(
                styles['tooltip'],
                'w-1 max-w-xs whitespace-normal break-words border-round-lg bg-white shadow-2',
              )}
              pt={{
                text: {
                  className: 'bg-white',
                },
              }}
            >
              <div className='flex flex-wrap gap-1 py-1 bg-white'>
                {rowData?.tags?.map((item) => {
                  return (
                    <Tag
                      className='bg-white text-900 font-medium px-2 py-0 border-1 border-round'
                      value={item.name}
                      key={item.name}
                    />
                  );
                })}
              </div>
            </Tooltip>
          </div>
        )}
      </div>
    );
  };

  const isTableEmpty =
    !isLoading &&
    data?.data.length === 0 &&
    Object.values(filters).filter((value) => {
      return Boolean(value);
    }).length === 0;

  // If no products, show the empty screen
  if (isTableEmpty) {
    return (
      <EmptyScreen imageSrc={EmptyProductsImg}>
        <div className='flex flex-column align-items-center p-4 gap-3'>
          <h4 className='m-0 p-0'>{t('productsPage.emptyScreen.title')}</h4>
          <span>{t('productsPage.emptyScreen.text')}</span>
          {!isBasfNonAdmin && (
            <Button
              label={t('productsPage.emptyScreen.buttons.addProduct')}
              onClick={() => {
                openDialog('add-product-dialog');
              }}
            />
          )}
        </div>
      </EmptyScreen>
    );
  }

  const pcfColumnHeaderTemplate = (): JSX.Element => {
    return (
      <div className='flex flex-column'>
        <span>{t('productsPage.productsTable.columns.pcf.header1')}</span>
        <span className='text-sm font-light'>
          {t('productsPage.productsTable.columns.pcf.header2')}
        </span>
      </div>
    );
  };

  return (
    <div className={styles['products-table']}>
      <DataTable
        value={data?.data}
        loading={isLoading}
        paginator
        paginatorTemplate={getPaginatorTemplate({
          totalRecords,
          rowsPerPage: size,
          skip,
          onNext: () => {
            setParams({ page: (skip + size) / size + 1 });
          },
          onPrev: () => {
            setParams({ page: (skip + size) / size - 1 });
          },
        })}
        rows={size}
        paginatorPosition='top'
        scrollable
        header={<ProductsHeader companyTags={companyTags ?? []} />}
        sortField={sortField}
        sortOrder={sortField ? (sortOrder === 'desc' ? -1 : 1) : undefined}
        onSort={(event: DataTableStateEvent) => {
          const newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
          setParams({
            sort: `${event.sortField}-${newSortOrder}`,
            page: 1,
          });
        }}
        lazy
        tableStyle={{ tableLayout: 'fixed', minWidth: '800px' }}
        emptyMessage={t('global.emptyTableMessage')}
      >
        <Column
          header={t('productsPage.productsTable.columns.productName')}
          body={productNameTemplate}
          style={{ width: '30%', wordBreak: 'break-word' }}
          sortable
          sortField='productName'
        />
        <Column
          field='productId'
          header={t('productsPage.productsTable.columns.productId')}
          style={{ width: '20%', wordBreak: 'break-word' }}
          sortable
          sortField='productId'
        />
        <Column
          header={t('productsPage.productsTable.columns.location')}
          body={locationTemplate}
          style={{ width: '20%' }}
          sortable
          sortField='location'
        />
        <Column
          header={pcfColumnHeaderTemplate}
          body={pcfTemplate}
          style={{ width: '15%' }}
          sortable
          sortField='pcf'
        />
        {canUseTags && (
          <Column
            header={t('productsPage.productsTable.columns.tags')}
            body={tagsTemplate}
            style={{
              width: '15%',
            }}
          />
        )}
      </DataTable>
    </div>
  );
};

export default ProductsTable;
